import { SparklineCell } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { abbrNumberWithThresholdFilter, changeFilter } from "app/filters/ngFilters";
import { trendItemsToSparklineData } from "app/pages/sales-intelligence/common-components/table-cells";
import { WEBSITE_ANALYSIS_PERFORMANCE } from "app/pages/sales-intelligence/constants/routes";
import { MIN_RELIABLE_METRIC_VALUE } from "app/pages/sales-intelligence/constants/threshold";
import companyAnalysisTrackingService from "app/pages/sales-intelligence/modules/company-analysis/services/tracking/companyAnalysisTrackingService";
import type { WebsitesDigitalAssetRecord } from "app/pages/sales-intelligence/modules/company-analysis/types/digital-assets";
import {
    StyledCardSubHeadingText,
    StyledExternalLink,
    StyledSingleDomainViewContainer,
    StyledVisitsChangeIcon,
} from "app/pages/sales-intelligence/pages/company-analysis/containers/overview/DigitalAssetsCard/styles";
import { proNavigator } from "single-spa/react-app/ProNavigator";

type Props = {
    digitalAsset: WebsitesDigitalAssetRecord;
    companyName: string;
};

export const SingleDomainView = ({ digitalAsset, companyName }: Props) => {
    const visits = abbrNumberWithThresholdFilter()({ min: MIN_RELIABLE_METRIC_VALUE }, digitalAsset.visits);
    const monthlyVisitsChangeMom = digitalAsset.monthlyVisitsChangeMom;
    const formattedMonthlyVisitsChangeMom = changeFilter()(Math.abs(monthlyVisitsChangeMom));
    const direction = monthlyVisitsChangeMom > 0;

    const graphCellData = trendItemsToSparklineData(digitalAsset.visitsHistory);

    const handleSiteClick = () => {
        companyAnalysisTrackingService.widgets.trackDigitalAssetsDomainClick(companyName, digitalAsset.domain);
    };

    return (
        <StyledSingleDomainViewContainer
            padding="86px 16px 18px"
            height="366px"
            flexDirection="column"
            alignItems="center"
            justifyContent="space-between"
        >
            <Flex
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
            >
                <Text fontVariant="h3">{visits}</Text>
                <StyledCardSubHeadingText fontVariant="report">
                    {translate("si.company-analysis.digital-assets-card.title")}
                </StyledCardSubHeadingText>
                <StyledExternalLink
                    href={proNavigator.href(WEBSITE_ANALYSIS_PERFORMANCE, {
                        key: digitalAsset.domain,
                        isWWW: "*",
                        duration: "3m",
                        country: 999,
                    })}
                    onMouseDown={handleSiteClick}
                >
                    {digitalAsset.domain}
                </StyledExternalLink>
                <Flex
                    alignItems="center"
                    gap="4px"
                >
                    {monthlyVisitsChangeMom && monthlyVisitsChangeMom !== 0 ? (
                        <>
                            <StyledVisitsChangeIcon iconName={direction ? "arrow-up" : "arrow-down"} />
                            <Text
                                fontVariant="input2"
                                color={direction ? colorsPalettes.green["400_P"] : colorsPalettes.red["400_P"]}
                            >
                                {formattedMonthlyVisitsChangeMom}
                            </Text>
                            <StyledCardSubHeadingText fontVariant="report">YoY</StyledCardSubHeadingText>
                        </>
                    ) : (
                        "-"
                    )}
                </Flex>
            </Flex>

            {graphCellData.length ? (
                <SparklineCell
                    type="area"
                    data={graphCellData as number[]}
                    height={160}
                    min={0}
                />
            ) : null}
        </StyledSingleDomainViewContainer>
    );
};
